import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabase';
import { useLeaderboardRows, type LeaderboardRow } from './useLeaderboardRows';
import { usePrimaryPositions } from './usePrimaryPositions';
import { runLabel } from '../components/ut/format';

/** Public stats for one player, as UT cards and rows show them. No skill ratings. */
export interface UtPlayerInfo {
  id: string;
  name: string;
  xp: number;
  rarity: string | null;
  rank: number | null;
  caps: number;
  winRate: number | null;
  wins: number;
  draws: number;
  losses: number;
  motm: number;
  streak: number;
  maxStreak: number;
  run: string;
  goalDifferential: number;
  position: string | null;
  bluePct: number | null;
  whatsappMember: boolean;
  /** Uploaded photo (players.photo_url), or null for a generated avatar / none. */
  photo?: string | null;
  row: LeaderboardRow | null;
}

/**
 * One index of public player stats for UT screens: the leaderboard rows
 * (XP, tier, W/D/L, streaks, GD), MOTM counts and primary positions. Players
 * with no caps aren't in the leaderboard rows, so `extraIds` fills their XP
 * and tier from player_xp.
 */
export function useUtPlayerIndex(extraIds: string[] = []): { index: Map<string, UtPlayerInfo>; loading: boolean; error: string | null } {
  const { rows, loading: rowsLoading, error } = useLeaderboardRows(true);
  const { positions, loading: posLoading } = usePrimaryPositions(null);
  const [motm, setMotm] = useState<Map<string, number>>(new Map());
  const [extras, setExtras] = useState<Map<string, { name: string; xp: number; rarity: string | null; caps: number; streak: number; maxStreak: number }>>(new Map());
  const [motmLoading, setMotmLoading] = useState(true);
  const [photos, setPhotos] = useState<Map<string, string>>(new Map());

  // Only a handful of players have photos, so fetch them all once.
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('players')
      .select('id, photo_url')
      .not('photo_url', 'is', null)
      .then(({ data }) => {
        if (cancelled) return;
        setPhotos(new Map(((data ?? []) as { id: string; photo_url: string }[]).map((p) => [p.id, p.photo_url])));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.rpc('get_motm_leaderboard', { p_limit: 1000 }).then(({ data }) => {
      if (cancelled) return;
      const m = new Map<string, number>();
      for (const r of (data ?? []) as { player_id: string; motm_count: number }[]) m.set(r.player_id, Number(r.motm_count) || 0);
      setMotm(m);
      setMotmLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const rowIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const missingKey = useMemo(
    () => (rowsLoading ? '' : extraIds.filter((id) => !rowIds.has(id)).sort().join(',')),
    [extraIds, rowIds, rowsLoading],
  );

  useEffect(() => {
    if (!missingKey) return;
    let cancelled = false;
    supabase
      .from('players')
      .select('id, friendly_name, caps, current_streak, max_streak, player_xp ( xp, rarity )')
      .in('id', missingKey.split(','))
      .then(({ data }) => {
        if (cancelled) return;
        const m = new Map();
        for (const p of (data ?? []) as Array<{
          id: string;
          friendly_name: string;
          caps: number | null;
          current_streak: number | null;
          max_streak: number | null;
          player_xp: { xp: number | null; rarity: string | null } | { xp: number | null; rarity: string | null }[] | null;
        }>) {
          const px = Array.isArray(p.player_xp) ? p.player_xp[0] : p.player_xp;
          m.set(p.id, {
            name: p.friendly_name,
            xp: px?.xp ?? 0,
            rarity: px?.rarity ?? null,
            caps: p.caps ?? 0,
            streak: p.current_streak ?? 0,
            maxStreak: p.max_streak ?? 0,
          });
        }
        setExtras(m);
      });
    return () => {
      cancelled = true;
    };
  }, [missingKey]);

  const index = useMemo(() => {
    const m = new Map<string, UtPlayerInfo>();
    for (const r of rows) {
      m.set(r.id, {
        id: r.id,
        name: r.friendlyName,
        xp: r.xp,
        rarity: r.rarity,
        rank: r.rank,
        caps: r.caps,
        winRate: r.winRate,
        wins: r.wins,
        draws: r.draws,
        losses: r.losses,
        motm: motm.get(r.id) ?? 0,
        streak: r.attendanceStreak,
        maxStreak: r.maxAttendanceStreak,
        run: runLabel(r),
        goalDifferential: r.goalDifferential,
        position: positions.get(r.id) ?? null,
        bluePct: r.bluePct,
        whatsappMember: r.whatsappMember,
        photo: photos.get(r.id) ?? null,
        row: r,
      });
    }
    for (const [id, e] of extras) {
      if (m.has(id)) continue;
      m.set(id, {
        id,
        name: e.name,
        xp: e.xp,
        rarity: e.rarity,
        rank: null,
        caps: e.caps,
        winRate: null,
        wins: 0,
        draws: 0,
        losses: 0,
        motm: motm.get(id) ?? 0,
        streak: e.streak,
        maxStreak: e.maxStreak,
        run: '—',
        goalDifferential: 0,
        position: positions.get(id) ?? null,
        bluePct: null,
        whatsappMember: false,
        photo: photos.get(id) ?? null,
        row: null,
      });
    }
    return m;
  }, [rows, motm, positions, extras, photos]);

  return { index, loading: rowsLoading || posLoading || motmLoading, error };
}
